import { Modal } from "react-bootstrap";
import Popup from "../../Helpers/Popup/Popup";
import PopupTitle from "../../Helpers/Popup/PopupParts/PopupTitle";
import { useGetSpecificSnagQuery } from "../../../Redux/service/Snag/Snag";
import trans from "../../../utils";
import Close from "../../Helpers/CloseBtn/Close";
import Loader from "../../Helpers/Loader/Loader";
import ViewSnagBody from "./ViewSnagBody";
import { useContext, useState } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { useSelector } from "react-redux";
import { Button } from "antd";
import RectifyPopup from "../ActionPopups/RectifyPopup";
import FixPopup from "../ActionPopups/FixPopup";
import VerifyPopup from "../ActionPopups/VerifyPopup";
import ClosePopup from "../ActionPopups/ClosePopup";
import ReopenPopup from "../ActionPopups/ReopenPopup";
import DisputePopup from "../ActionPopups/DisputePopup";

const ViewSnag = ({ snagId }) => {
	const { handleClose } = useContext(popupcontext);
	const { data, isFetching, refetch } = useGetSpecificSnagQuery(snagId, {
		skip: !snagId,
		refetchOnMountOrArgChange: true,
	});
	const user = useSelector((state) => state.users.user);
	const OfflineMode = useSelector((state) => state.users.offlineMode);
	const snag = data?.data || null;

	// Get user info
	const userId = user?.id;
	const userRole = user?.attributes?.role;

	// State for popups
	const [showFix, setShowFix] = useState(false);
	const [showRectify, setShowRectify] = useState(false);
	const [showVerify, setShowVerify] = useState(false);
	const [showClose, setShowClose] = useState(false);
	const [showReopen, setShowReopen] = useState(false);
	const [showDispute, setShowDispute] = useState(false);

	// Callback to refetch data after successful action
	const handleActionSuccess = () => {
		refetch();
	};

	// =====================================================
	// BUTTON VISIBILITY LOGIC (Based on Frontend Guide)
	// =====================================================

	const getAvailableButtons = () => {
		if (!snag || !userRole) return [];

		const buttons = [];
		const status = snag.status;
		const snagType = snag.snag_type;
		const isCreator = snag.reported_by_id === userId;

		// ✅ FIX Button (Subcontractor only)
		if (userRole === "subContractor") {
			if (["OPEN", "REOPEN"].includes(status)) {
				buttons.push({
					key: "fix",
					label: trans("Mark as Fixed"),
					type: "primary",
					onClick: () => setShowFix(true),
				});
			}
			return buttons; // Subcontractor only sees FIX button
		}

		// ✅ RECTIFY Button (Contractor OR QA for EXTERNAL)
		if (userRole === "contractor") {
			if (["OPEN", "FIX", "REOPEN"].includes(status)) {
				buttons.push({
					key: "rectify",
					label: trans("Rectify"),
					type: "primary",
					onClick: () => setShowRectify(true),
				});
			}
		} else if (userRole === "qa") {
			// QA can RECTIFY only EXTERNAL snags from OPEN or REOPEN
			if (
				snagType === "EXTERNAL" &&
				["OPEN", "REOPEN", "FIX"].includes(status)
			) {
				buttons.push({
					key: "rectify",
					label: trans("Rectify"),
					type: "primary",
					onClick: () => setShowRectify(true),
				});
			}
		}

		// ✅ VERIFY Button (QA only, EXTERNAL only, RECTIFY status only)
		if (
			userRole === "qa" &&
			snagType === "EXTERNAL" &&
			(status === "RECTIFY" || status === "FIX")
		) {
			buttons.push({
				key: "verify",
				label: trans("Verify"),
				type: "primary",
				onClick: () => setShowVerify(true),
			});
		}

		// ✅ CLOSE Button (Complex logic)
		let canClose = false;

		// // Creator privilege (highest priority)
		// if (isCreator && ["qa", "consultant"].includes(userRole)) {
		//   canClose = true;
		// }
		// QA can close INTERNAL snags
		if (
			userRole === "qa" &&
			snagType === "INTERNAL" &&
			["OPEN", "FIX", "RECTIFY", "REOPEN", "VERIFY"].includes(status)
		) {
			canClose = true;
		}
		// Consultant can close EXTERNAL snags from any status (except CLOSED)
		else if (
			userRole === "consultant" &&
			snagType === "EXTERNAL" &&
			status !== "CLOSED" &&
			status !== "IN_DISPUTE"
		) {
			canClose = true;
		} else if (
			userRole === "qa" &&
			snagType === "INTERNAL" &&
			status !== "CLOSED"
		) {
			canClose = true;
		}

		if (canClose) {
			buttons.push({
				key: "close",
				label: trans("Close"),
				type: "default",
				onClick: () => setShowClose(true),
			});
		}

		// ✅ REOPEN Button
		if (userRole === "qa" && snagType === "INTERNAL") {
			if (["RECTIFY", "CLOSED", "IN_DISPUTE", "FIX"].includes(status)) {
				buttons.push({
					key: "reopen",
					label: trans("Reopen"),
					type: "default",
					danger: true,
					onClick: () => setShowReopen(true),
				});
			}
		} else if (userRole === "consultant" && snagType === "EXTERNAL") {
			if (["VERIFY", "CLOSED"].includes(status)) {
				buttons.push({
					key: "reopen",
					label: trans("Reopen"),
					type: "default",
					danger: true,
					onClick: () => setShowReopen(true),
				});
			}
		} else if (userRole === "qa" && snagType === "EXTERNAL") {
			if (["IN_DISPUTE", "FIX", "RECTIFY", "VERIFY"].includes(status)) {
				buttons.push({
					key: "reopen",
					label: trans("Reopen"),
					type: "default",
					danger: true,
					onClick: () => setShowReopen(true),
				});
			}
		}

		// ✅ DISPUTE Button (QA only, not IN_DISPUTE or CLOSED)
		if (userRole === "qa" && !["IN_DISPUTE", "CLOSED"].includes(status)) {
			buttons.push({
				key: "dispute",
				label: trans("Mark as Disputed"),
				type: "default",
				onClick: () => setShowDispute(true),
			});
		}

		return buttons;
	};

	const availableButtons = getAvailableButtons();

	return (
		<>
			<Popup show={!!snagId && !OfflineMode} size={"lg"}>
				<Modal.Header className="modal-header flex-between">
					<Modal.Title id="contained-modal-title-vcenter">
						<PopupTitle headtitle={<div>#{snag?.snag_number}</div>} />
					</Modal.Title>
					<Close
						style="close-32 close"
						close={() => handleClose("display")}
						img="Close"
					/>
				</Modal.Header>
				<Modal.Body className="modal-body-scrollable fixed-popup-height">
					{isFetching ? (
						<div className="loader-container">
							<Loader />
						</div>
					) : (
						<ViewSnagBody data={snag} snagId={snagId} />
					)}
				</Modal.Body>
				<Modal.Footer>
					<div className="popups-footer gap-8">
						{availableButtons.map((button) => (
							<Button
								key={button.key}
								type={button.type}
								danger={button.danger}
								onClick={button.onClick}
							>
								{button.label}
							</Button>
						))}
					</div>
				</Modal.Footer>
			</Popup>

			{/* All Action Popups */}
			{showFix && (
				<FixPopup
					show={showFix}
					setShow={setShowFix}
					snagId={snagId}
					onSuccess={handleActionSuccess}
				/>
			)}

			{showRectify && (
				<RectifyPopup
					show={showRectify}
					setShow={setShowRectify}
					snagId={snagId}
					onSuccess={handleActionSuccess}
				/>
			)}

			{showVerify && (
				<VerifyPopup
					show={showVerify}
					setShow={setShowVerify}
					snagId={snagId}
					onSuccess={handleActionSuccess}
				/>
			)}

			{showClose && (
				<ClosePopup
					show={showClose}
					setShow={setShowClose}
					snagId={snagId}
					onSuccess={handleActionSuccess}
				/>
			)}

			{showReopen && (
				<ReopenPopup
					show={showReopen}
					setShow={setShowReopen}
					snagId={snagId}
					onSuccess={handleActionSuccess}
				/>
			)}

			{showDispute && (
				<DisputePopup
					show={showDispute}
					setShow={setShowDispute}
					snagId={snagId}
					onSuccess={handleActionSuccess}
				/>
			)}
		</>
	);
};

export default ViewSnag;
